// CRM 2.0 page (M1-F9 shell, M1-F10 cards) — tabs as prototype crm2Page / s2Page / cxPage.
// Top tabs: Dashboard (M20, Pratik) · Application for Booking · Agreement Preparation ·
// Agreement in Process · Agreement Complete · Cancellations. Each booking tab loads its
// CRM 2.0 cards from GET /crm2/list/:tab with counts per sub-tab (red when late).
// Changing a tab or sub-tab clears the filters (DC-143). M1-F11: Filter by (prototype
// categories + the document's CP, SM, SDR, OCR, AV, Emandate) and the card ⋮ menu,
// which reuses today's pop-ups; Send Email To Customer / Send Test Mail use the CRM 2.0
// Application email (M2-F2). Items that need later modules (
// Change Booking Amount, Move to CRM Review, Change Unit, Cancel Unit, Reassign CRM,
// Accept / Reject / Claim) are added when those are built. Opened from the "CRM 2.0" menu.
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import action from "../../Redux Store/actions";
import EmptyState from "../../Assets/Images/Common/noProjectFound.svg";
import css from "../../Components/Crm2/ui/Crm2Cards.module.css";
import Crm2BookingCard from "../../Components/Crm2/BookingCard/Crm2BookingCard";
import Crm2AgreementCard from "../../Components/Crm2/AgreementCard/Crm2AgreementCard";
import Crm2CancellationCard from "../../Components/Crm2/CancellationCard/Crm2CancellationCard";
import FilterBy from "../../Components/Crm2/ui/FilterBy";
import { avChanged, kycDone, ocrText, sdrDone } from "../../Components/Crm2/ui/cardParts";
import { crmDetailsObject } from "../../Components/Crm2/ui/crmDetailsRow";
import {
  CRM2_LIST_TAB,
  crm2AcceptTag,
  crm2AvSubmit,
  crm2ChangeBookingAmount,
  crm2ChangeUnitDecide,
  crm2Claim,
  crm2DecideApproval,
  crm2MoveToCrmReview,
  crm2RejectTag,
  getCrm2List,
  getOldCrmRow,
  sendCrm2ApplicationEmail,
} from "../../Services/Crm2List";
import { getSdrPercentageAndLegalCharges } from "../../Services/AllAllotments";
import ChangePriceModal from "../../Components/AllAllotments/ChangePriceModal/ChangePriceModal";
import ChangeBookingAmountModal from "../../Components/AllAllotments/CrmAllotment/ChangeBookingAmountModal/ChangeBookingAmountModal";
import Crm2ParkingModal from "../../Components/Crm2/Parking/Crm2ParkingModal";
import Crm2OldParkingModal from "../../Components/Crm2/Parking/Crm2OldParkingModal";
import Crm2ChangeUnitModal from "../../Components/Crm2/ChangeUnit/Crm2ChangeUnitModal";
import RejectionReasonModal from "../../Components/AllAllotments/CrmAllotment/RejectionReasonModal/RejectionReasonModal";
import ConfirmationAlert from "../../Subcomponents/ConfirmationAlert";
import { dismissLoader, presentLoader } from "../../Services/loader";
import { errorTost, successTost } from "../../Services/Toast";
import EnterEmailModal from "../../Components/AllAllotments/CrmAllotment/EnterEmailModal/EnterEmailModal";
import ViewDocumentsModal from "../../Components/AllAllotments/CrmAllotment/ViewDocumentsModal/ViewDocumentsModal";
import ViewAllotedCrmDetailsModal from "../../Components/AllAllotments/CrmAllotment/ViewAllotedCrmDetailsModal/ViewAllotedCrmDetailsModal";
import CrmTimeLineModal from "../../Components/AllAllotments/CrmAllotment/CRMTimeLineModal/CrmTimeLineModal";
import UpdateLeadDetails from "../../Components/AllAllotments/CrmAllotment/CheckVirtualAccountStatus/CheckVirtualAccountStatus";
import PreviewAggrementConfirmationModal from "../../Components/AllAllotments/CrmAllotment/PreviewAggrementConfirmationModal/PreviewAggrementConfirmationModal";
import RemoveLoyaltyModal from "../../Components/Crm2/Discounts/RemoveLoyaltyModal";

const TOP_TABS = ["Dashboard", "Application for Booking", "Agreement Preparation", "Agreement in Process", "Agreement Complete", "Cancellations"];
const SUB_TABS = {
  "Application for Booking": ["Unclaimed", "Tag Pending", "CRM Review", "Approval Pending", "Parking Selection", "Final Review"],
  "Agreement in Process": ["All", "Print & Sign", "Rider & Registration"],
  "Agreement Complete": ["All", "Return & Handover", "Handed over"],
  Cancellations: ["Cancellation Pending", "Cancellation Completed"],
};
const DEFAULT_SUB = { "Application for Booking": "CRM Review", "Agreement in Process": "All", "Agreement Complete": "All", Cancellations: "Cancellation Pending" };

const NAME_UNIT_SORTS = ["Latest First", "Oldest First", "Name Ascending", "Name Descending", "Unit No Ascending", "Unit No Descending"];
const SORTS = {
  "Application for Booking": ["Fewest days left", ...NAME_UNIT_SORTS],
  "Agreement Preparation": ["Fewest days left", "Appointment date", ...NAME_UNIT_SORTS],
  "Agreement in Process": ["Appointment date", ...NAME_UNIT_SORTS],
  "Agreement Complete": NAME_UNIT_SORTS,
  Cancellations: NAME_UNIT_SORTS,
};

// Filter by categories per tab (M1.27 / M1.41; prototype FCATS … FCATS5). Values are
// read from the cards; the counts-row links (Email not sent, Waiting for Sanction) set
// the Email / Sanction categories.
const emandate = (c) => (c.isEmandateEligible && c.crmEmandateStatus ? c.crmEmandateStatus : "Not applicable");
const COMMON = { Projects: (c) => c.projectName, CRM: (c) => c.crmName };
const CATS = {
  "Application for Booking": {
    ...COMMON,
    CP: (c) => c.cpName,
    SM: (c) => c.smName,
    "Lead Type": (c) => c.leadType,
    "Days left": (c) => (!c.ring?.started ? "Not started" : c.ring.daysLeft <= 2 ? "0–2 days" : c.ring.daysLeft <= 5 ? "3–5 days" : "6–15 days"),
    Email: (c) => (c.isEmailSent === true ? "Email Sent" : "Email not sent"),
    "Agreement KYC": (c) => (kycDone(c) ? "Done" : "Pending"),
    SDR: (c) => (sdrDone(c) ? "SDR Complete" : "SDR Pending"),
    OCR: ocrText,
    AV: (c) => (avChanged(c) ? "AV Changed" : "AV Not Changed"),
    Sanction: (c) => (c.isSanctioned === false ? "Waiting for Sanction" : "Sanctioned"),
  },
  "Agreement Preparation": {
    Step: (c) => c.step?.[0],
    ...COMMON,
    "Days left": (c) => (c.ring?.kind !== "STAGE2" ? "Clock stopped" : c.ring.daysLeft <= 5 ? "0–5 days" : c.ring.daysLeft <= 15 ? "6–15 days" : "16–45 days"),
    "CF NOC": (c) => (!c.cfNocStatus || c.cfNocStatus === "NOT_NEEDED" ? "Not needed" : c.cfNocStatus === "RECEIVED" ? "Received" : "Waiting"),
    "Lead Type": (c) => c.leadType,
    Emandate: emandate,
  },
  "Agreement in Process": {
    Step: (c) => c.step?.[0],
    ...COMMON,
    "Sub-Registrar office": (c) => c.appointment?.office,
    Registration: (c) => {
      const n = c.ring?.kind === "APPOINTMENT" ? c.ring.daysTo : null;
      return n === null ? "No date" : n < 0 ? "Overdue" : n === 0 ? "Today" : n <= 7 ? "Next 7 days" : "Later";
    },
    Rider: (c) => c.riderName || "Not assigned",
    Emandate: emandate,
  },
  "Agreement Complete": { ...COMMON, "Sub-Registrar office": (c) => c.appointment?.office, "Lead Type": (c) => c.leadType, Emandate: emandate },
  Cancellations: {
    Status: (c) => c.cancelStatus?.[0],
    From: (c) => c.cancelFrom?.[0],
    Stage: (c) => c.cancelFrom?.[1],
    Reprice: (c) => (c.cancel?.stage !== "CANCELLED" ? "Not cancelled yet" : c.cancel?.isSkipReprice ? "Reprice Skipped" : c.cancel?.unitPriceAlreadyUpdated ? "Reprice Done" : "Reprice Pending"),
    ...COMMON,
  },
};
// Filter by › Date: which date each tab filters on (none on Cancellations).
const DATE_OF = {
  "Application for Booking": ["Applied On", (c) => c.stage1Day0At],
  "Agreement Preparation": ["Final Review approved", (c) => c.finalReviewApprovedAt],
  "Agreement in Process": ["Appointment date", (c) => c.appointment?.date],
  "Agreement Complete": ["Agreement Date", (c) => c.agreementDate],
};
function inDate(c, top, d) {
  if (!DATE_OF[top] || (!d.from && !d.to)) return true;
  const at = Number(DATE_OF[top][1](c));
  if (!at) return false;
  if (d.from && at < new Date(`${d.from}T00:00:00`).getTime()) return false;
  if (d.to && at > new Date(`${d.to}T23:59:59`).getTime()) return false;
  return true;
}

// Card ⋮ menu (M1.28, prototype menuFor / s2Menu): today's pop-ups that work on a CRM 2.0
// booking now. The other prototype items are added by the functions that build them.
// perm (M5): { bookingAmount, moveToReview, changeUnit } from today's keys / CRM 2.0 › Change Unit · Raise
function menuFor(c, top, head = false, perm = {}) {
  if (top === "Cancellations") return ["Timeline And Notes", "View CRM Details", "Check Virtual Account Status"];
  if (top !== "Application for Booking")
    return ["View Cost Sheet", "View CRM Details", "Timeline And Notes", "Check Virtual Account Status", "Preview Agreement Confirmation Form", "Send Email To Customer", "Send Test Mail"];
  if (c.sub === "Unclaimed") return [];
  const m = ["View CRM Details", "Timeline And Notes", "Check Virtual Account Status"];
  if (c.sub !== "Tag Pending") {
    if (kycDone(c)) m.push("Preview Agreement Confirmation Form");
    m.push("Send Email To Customer", "Send Test Mail", "View Cost Sheet");
    // M5-F8 / DC-123: only while stamp duty & charges are unpaid; Move to CRM Review only from these three tabs
    if (perm.bookingAmount && !sdrDone(c)) m.push("Change Booking Amount");
    if (perm.moveToReview && ["Approval Pending", "Parking Selection", "Final Review"].includes(c.sub) && !c.changeUnit) m.push("Move to CRM Review");
  }
  // M5-F9 (DC-181): until Final Review approval (the allotment letter)
  if (perm.changeUnit && !c.finalReviewApprovedAt && !c.changeUnit) m.push("Change Unit");
  // M4-F9 (DC-108 / DC-153): CRM Head only; existing VJ customer is an applicant (applied or offered); until Final Review approval
  if (head && c.sub !== "Tag Pending" && c.discounts?.loyaltyRemovable && !c.finalReviewApprovedAt) m.push("Remove Loyalty Discount");
  return m;
}
// Today's CRM tab name for a crmStatusId — the Preview Agreement Confirmation Form pop-up
// reads its Download permission under All Allotments › CRM › <tab>.
const NO_CATS = {};
const OLD_TAB = { 0: "Tag Pending", 1: "Change AV", 3: "Changed Plan", 4: "Parking Selection", 2: "Update SDR", 5: "Agreement", 6: "Completed" };

const BLUE = ["#EFF8FF", "#B2DDFF", "#175CD3"];
const GREEN = ["#ECFDF3", "#ABEFC6", "#027A48"];
const AMBER = ["#FFFAEB", "#FEDF89", "#B54708"];

function bannerFor(top, sub, counts) {
  if (top === "Application for Booking" && sub === "Unclaimed")
    return [AMBER, "Bookings come here when a project has no active CRM (the CRM Head is told at once) or when the project uses the Unclaimed tab (Settings › CRM 2.0 › Days and rules). The CRM Head is also told when a booking waits here longer than 1 working day (setting)."];
  if (top === "Application for Booking" && sub === "Tag Pending") return [BLUE, "Only for projects with the acceptance step switched on (Project › CRM 2.0 › Days and rules)."];
  if (top === "Agreement Preparation")
    return [BLUE, "From Final Review to Process for Stamp: Allotment Letter eSign, FarVision Sync, CF NOC, final draft OTP, appointment, stamp duty. Each card shows its step and the one thing to do next; use Filter by › Step to see one step."];
  if (top === "Agreement in Process")
    return [
      BLUE,
      sub === "All"
        ? "All: every booking in Agreement in Process in one list, by appointment date: printing, VJ signature, Rider and registration. You only follow here (and can reschedule); the status changes in real time as the Document Manager and the Rider scan. Use Filter by › Status to narrow it."
        : sub === "Print & Sign"
          ? "Print & Sign: the Document Manager prints the one original and gets it signed by VJ's authorised signatory. You follow it here; late steps turn red and the Document Manager is told. Exceptions also show here."
          : "Rider & Registration: the Document Manager assigns the Rider and hands the signed original over; the Rider records the registration. You can reschedule the appointment.",
    ];
  if (top === "Agreement Complete")
    return [
      GREEN,
      sub === "All"
        ? "All: every registered booking in one list, with where the original is now: with the Rider, at the Document Desk ready to collect, or handed over to the customer or banker."
        : sub === "Return & Handover"
          ? "Registered bookings (Agreement done). The original is still with the Rider or ready to collect at the Document Desk; the Document Manager hands it to the customer or banker."
          : "Registered bookings whose original has been handed over. Same as today's Completed tab, for CRM 2.0 projects.",
    ];
  if (top === "Cancellations")
    return sub === "Cancellation Pending"
      ? [AMBER, `Open cancellations until every approval is done. ${counts?.waitingForYou || 0} wait for you.`]
      : [GREEN, "Cancelled and unit released. The refund can still be in progress."];
  return null;
}

const num = (v) => Number(v) || 0;
function daysLeft(c) {
  if (c.ring?.kind === "STAGE2") return c.ring.daysLeft;
  if (c.ring && c.ring.started) return c.ring.daysLeft;
  return 99;
}
function onDate(c, top) {
  if (top === "Cancellations") return num(c.cancel?.raisedAt);
  if (top === "Application for Booking") return num(c.stage1Day0At || c.crmCreatedAt);
  return num(c.agreementDate || c.finalReviewApprovedAt);
}
const apptAt = (c) => (c.appointment?.date ? num(c.appointment.date) + num(c.appointment.half) : 9e15);

function sortCards(list, sort, top) {
  const by = {
    "Fewest days left": (a, b) => daysLeft(a) - daysLeft(b),
    "Appointment date": (a, b) => apptAt(a) - apptAt(b),
    "Latest First": (a, b) => onDate(b, top) - onDate(a, top),
    "Oldest First": (a, b) => onDate(a, top) - onDate(b, top),
    "Name Ascending": (a, b) => String(a.leadName || "").localeCompare(String(b.leadName || "")),
    "Name Descending": (a, b) => String(b.leadName || "").localeCompare(String(a.leadName || "")),
    "Unit No Ascending": (a, b) => String(a.unitNo || "").localeCompare(String(b.unitNo || ""), undefined, { numeric: true }),
    "Unit No Descending": (a, b) => String(b.unitNo || "").localeCompare(String(a.unitNo || ""), undefined, { numeric: true }),
  }[sort];
  return by ? [...list].sort(by) : list;
}

function Empty({ text }) {
  return (
    <div className={css.empty}>
      <img src={EmptyState} alt="No Data" />
      <span>{text}</span>
    </div>
  );
}

function TabRow({ tabs, value, onChange, counts, late }) {
  return (
    <div className={css.tabs} role="tablist">
      {tabs.map((t) => (
        <button key={t} type="button" role="tab" aria-selected={t === value} className={`${css.tab} ${t === value ? css.tabOn : ""}`} onClick={() => onChange(t)}>
          {t}
          {counts && (
            <span className={`${css.badge} ${t === "Unclaimed" || late?.[t] ? css.badgeLate : ""}`}>{counts[t] || 0}</span>
          )}
        </button>
      ))}
    </div>
  );
}

function Crm2Page() {
  const dispatch = useDispatch();
  const loggedInUser = useSelector((state) => state.Auth.user);
  // M2-F6: Accept / Reject on Tag Pending cards — the tagged CRM, the CRM Head (CRM 2.0 › Reassign CRM) or Super Admin.
  const canDecide = (c) =>
    loggedInUser?.roleId?.level == 1 ||
    c.crmUserId === loggedInUser?.userId ||
    !!loggedInUser?.roleId?.accessibleTabs?.["CRM 2.0"]?.["Reassign CRM"]?.isAccessible;
  // M2-F7: Claim on Unclaimed cards — CRM users (or Super Admin) with CRM 2.0 › Unclaimed · Claim
  const canClaim =
    loggedInUser?.roleId?.level == 1 ||
    (loggedInUser?.isCrmUser === true && !!loggedInUser?.roleId?.accessibleTabs?.["CRM 2.0"]?.["Unclaimed · Claim"]?.isAccessible);
  // M4-F9: CRM 2.0 › Remove Loyalty Discount (CRM Head), or Super Admin
  const canRemoveLoyalty = loggedInUser?.roleId?.level == 1 || !!loggedInUser?.roleId?.accessibleTabs?.["CRM 2.0"]?.["Remove Loyalty Discount (CRM Head)"]?.isAccessible;
  // M5: today's CRM tab keys (All Allotments › CRM › …) and CRM 2.0 › Change Unit keys; Super Admin has all
  const crmKey = (tab, btn) => loggedInUser?.roleId?.level == 1 || !!loggedInUser?.roleId?.accessibleTabs?.["All Allotments"]?.CRM?.[tab]?.[btn]?.isAccessible;
  const m5can = {
    previewApprove: crmKey("Change AV", "Preview And Approve"),
    preview: crmKey("Change AV", "Preview"),
    approve: crmKey("Changed Plan", "Approve"),
    approveOcr: crmKey("Changed Plan", "Approve OCR"),
    selectParking: crmKey("Parking Selection", "Select Parking"),
    changeUnit:
      loggedInUser?.roleId?.level == 1 ||
      !!loggedInUser?.roleId?.accessibleTabs?.["All Allotments"]?.["Change Unit Approver"] ||
      !!loggedInUser?.roleId?.accessibleTabs?.["CRM 2.0"]?.["Change Unit · Approve (apply directly)"]?.isAccessible,
  };
  const m5perm = {
    bookingAmount: crmKey("Change AV", "Change Booking Amount") || crmKey("Update SDR", "Change Booking Amount"),
    moveToReview: crmKey("Changed Plan", "Move to Change AV") || crmKey("Parking Selection", "Move to Change AV") || crmKey("Update SDR", "Move to Change AV"),
    changeUnit: loggedInUser?.roleId?.level == 1 || !!loggedInUser?.roleId?.accessibleTabs?.["CRM 2.0"]?.["Change Unit · Raise"]?.isAccessible,
  };
  const [top, setTop] = useState(TOP_TABS[0]);
  const [sub, setSub] = useState({});
  const [lists, setLists] = useState({});
  const [sort, setSort] = useState("");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState({});
  const [date, setDate] = useState({ from: "", to: "" });
  const [pop, setPop] = useState(null); // { item, row, card } — the ⋮ pop-up that is open

  const clearFilters = () => {
    setSel({});
    setDate({ from: "", to: "" });
  };

  // ⋮ menu: fetch the booking in today's CRM list shape, then open today's pop-up.
  // Send Email To Customer / Send Test Mail use the CRM 2.0 sender (M2-F2), no old row needed.
  const onMenu = async (item, card) => {
    console.log("[crm2][Crm2Page] menu", { item, allotmentPaymentId: card.allotmentPaymentId, crmStatusId: card.crmStatusId });
    if (["Send Email To Customer", "Send Test Mail", "Remove Loyalty Discount", "Move to CRM Review", "Change Unit"].includes(item)) {
      setPop({ item, card });
      return;
    }
    presentLoader();
    try {
      const row = await getOldCrmRow(card);
      if (!row) {
        errorTost("Could not open this booking. Please refresh and try again.");
        return;
      }
      setPop({ item, row, card });
    } catch (err) {
      console.error("[crm2][Crm2Page] menu failed", { item, allotmentPaymentId: card.allotmentPaymentId, error: err?.message });
      errorTost("Could not open this booking. Please try again.");
    } finally {
      dismissLoader();
    }
  };

  // M2-F2: Application for Booking email. email = test address (Send Test Mail) or null (customer).
  const sendEmail = async (email = null) => {
    const card = pop?.card;
    setPop(null);
    if (!card) return;
    presentLoader();
    try {
      const res = await sendCrm2ApplicationEmail(card.allotmentPaymentId, email);
      if (!email) load(top); // the Email chip, the step and the card message change
      if (res?.success === false) {
        // M2-F3: not sent yet, queued for an automatic retry (202)
        console.warn("[crm2][Crm2Page] application email queued for retry", { allotmentPaymentId: card.allotmentPaymentId, status: res.status, tries: res.tries, lastError: res.lastError });
        errorTost(res.message || "Email not sent yet. It will be tried again automatically.");
        return;
      }
      console.log("[crm2][Crm2Page] application email sent", { allotmentPaymentId: card.allotmentPaymentId, test: !!email, cc: res?.cc?.length, crmHeadCc: res?.crmHeadCc });
      successTost(email ? `Test email sent to ${email}.` : "Email sent successfully to the customer.");
    } catch (err) {
      const message = err?.response?.data?.message || "Email not sent. Please try again.";
      console.error("[crm2][Crm2Page] application email failed", { allotmentPaymentId: card.allotmentPaymentId, test: !!email, status: err?.response?.status, message });
      errorTost(message);
    } finally {
      dismissLoader();
    }
  };

  // M2-F6: Accept → CRM Review, Day 0, email; Reject (reason) → next CRM in turn.
  const decideTag = async (what, reason = "") => {
    const card = pop?.card;
    if (!card) return;
    if (what === "reject" && !String(reason).trim()) {
      errorTost("Please enter the rejection reason");
      return;
    }
    setPop(null);
    presentLoader();
    try {
      const res =
        what === "claim" ? await crm2Claim(card.allotmentPaymentId) : what === "accept" ? await crm2AcceptTag(card.allotmentPaymentId) : await crm2RejectTag(card.allotmentPaymentId, reason);
      console.log("[crm2][Crm2Page] tag decision", { what, allotmentPaymentId: card.allotmentPaymentId, status: res?.status });
      if (what === "claim") successTost(res?.email === "SENT" ? "Booking claimed. Email sent." : "Booking claimed. The email will be tried again automatically.");
      else if (what === "accept") successTost(res?.email === "SENT" ? "Booking accepted. Email sent." : "Booking accepted. The email will be tried again automatically.");
      else successTost(res?.status === "UNCLAIMED" ? "Tag rejected. No other CRM left — it is now Unclaimed." : `Tag rejected. Passed to ${res?.nextCrmName || "the next CRM"}.`);
      load(top);
    } catch (err) {
      const message = err?.response?.data?.message || "Something went wrong, please try again";
      console.error("[crm2][Crm2Page] tag decision failed", { what, allotmentPaymentId: card.allotmentPaymentId, status: err?.response?.status, message });
      errorTost(message);
    } finally {
      dismissLoader();
    }
  };

  // M5 card buttons: Preview and Approve / Preview (CRM Review), Approve / Reject Changes / Approve OCR and the
  // Change Unit Approve / Reject (Approval Pending boxes), Select Parking (Parking Selection).
  const onAct = async (card, what, extra = null) => {
    console.log("[crm2][Crm2Page] action", { what, allotmentPaymentId: card.allotmentPaymentId, sub: card.sub });
    if (!["plan", "planView", "avApprove"].includes(what)) {
      setPop({ item: what, card, extra });
      return;
    }
    // today's Change Plan pop-up reads the booking in today's CRM list shape and the project's stamp duty figures
    presentLoader();
    try {
      const row = await getOldCrmRow(card);
      if (!row) {
        errorTost("Could not open this booking. Please refresh and try again.");
        return;
      }
      const res = await getSdrPercentageAndLegalCharges(row.project_id, row.unit_id);
      const f = res?.data?.[0] || {};
      // Old Value = the booking's price now (discounts and earlier changes included)
      const priced = { ...row, old_unit_cost: card.unitCost || row.old_unit_cost, unitcost: card.unitCost || row.unitcost };
      setPop({ item: what, card, extra, row: priced, sdr: { sdrPercentage: f.sdrPercentage, legalCharges: f.legalCharges, registrationCharges: f.registrationCharges || 30000 } });
    } catch (err) {
      console.error("[crm2][Crm2Page] action open failed", { what, allotmentPaymentId: card.allotmentPaymentId, error: err?.message });
      errorTost("Could not open this booking. Please try again.");
    } finally {
      dismissLoader();
    }
  };

  // one server step from a pop-up, then reload the list
  const runM5 = async (label, fn, ok) => {
    setPop(null);
    presentLoader();
    try {
      const res = await fn();
      console.log("[crm2][Crm2Page] done", { label, status: res?.status });
      successTost(typeof ok === "function" ? ok(res) : ok);
      load(top);
    } catch (err) {
      const message = err?.response?.data?.message || "Something went wrong, please try again";
      console.error("[crm2][Crm2Page] failed", { label, status: err?.response?.status, message });
      errorTost(message);
      if (err?.response?.data?.code === "STALE") load(top);
    } finally {
      dismissLoader();
    }
  };
  const planToast = (r) =>
    r?.status === "AV_PENDING" ? "AV changed successfully!" : r?.status === "OCR_PENDING" ? "Plan approved. OCR below 10%: sent for OCR approval" : r?.parking === "AUTO_SKIPPED" ? "AV submitted successfully! No parking for this unit: moved to Final Review" : "AV submitted successfully!";

  useEffect(() => {
    dispatch(action.activeMenu("crm2"));
    console.log("[crm2][Crm2Page] opened");
  }, []);

  const load = (t) => {
    if (!CRM2_LIST_TAB[t]) return;
    setLists((l) => ({ ...l, [t]: { ...(l[t] || {}), loading: true, error: null } }));
    getCrm2List(t)
      .then((data) => {
        console.log("[crm2][Crm2Page] list loaded", { tab: t, rows: data.cards?.length, counts: data.counts });
        setLists((l) => ({ ...l, [t]: { loading: false, error: null, data } }));
      })
      .catch((err) => {
        const message = err?.response?.data?.message || "Could not load the bookings";
        console.error("[crm2][Crm2Page] list failed", { tab: t, status: err?.response?.status, message });
        setLists((l) => ({ ...l, [t]: { loading: false, error: message, data: null } }));
      });
  };

  useEffect(() => {
    if (top !== "Dashboard") load(top);
    setSort(SORTS[top]?.[0] || "");
    clearFilters();
    setQ("");
  }, [top]);

  const subs = SUB_TABS[top];
  const currentSub = subs ? sub[top] || DEFAULT_SUB[top] || subs[0] : null;
  const state = lists[top] || {};
  const data = state.data;

  const changeSub = (t) => {
    setSub((s) => ({ ...s, [top]: t }));
    clearFilters();
  };
  const cats = CATS[top] || NO_CATS;

  const inTab = useMemo(() => (data?.cards || []).filter((c) => !currentSub || currentSub === "All" || c.sub === currentSub), [data, currentSub]);
  const shown = useMemo(() => {
    const term = q.trim().toLowerCase();
    const l = inTab.filter(
      (c) =>
        Object.entries(sel).every(([k, v]) => !v.length || !cats[k] || v.includes(cats[k](c))) &&
        inDate(c, top, date) &&
        (!term || `${c.leadName || ""} ${c.wingName || ""} ${c.unitNo || ""}`.toLowerCase().includes(term)),
    );
    return sortCards(l, sort, top);
  }, [inTab, sel, date, q, sort, top, cats]);

  const dateOn = !!(DATE_OF[top] && (date.from || date.to));
  const nSel = Object.values(sel).reduce((a, v) => a + v.length, 0) + (dateOn ? 1 : 0);
  const emailNotSent = top === "Application for Booking" ? inTab.filter((c) => c.sub !== "Tag Pending" && c.sub !== "Unclaimed" && c.isEmailSent !== true).length : 0;
  const waitingSanction = top === "Application for Booking" ? inTab.filter((c) => c.isSanctioned === false).length : 0;
  const counts = data?.counts ? { ...data.counts, waitingForYou: (data.cards || []).filter((c) => c.cancel?.stage === "WITH_CRM").length } : null;
  const banner = top !== "Dashboard" ? bannerFor(top, currentSub, counts) : null;

  const Card = top === "Application for Booking" ? Crm2BookingCard : top === "Cancellations" ? Crm2CancellationCard : Crm2AgreementCard;
  const tabSlug = CRM2_LIST_TAB[top];

  return (
    <div className="pageWrapper" style={{ padding: "0 1.6rem" }}>
      <TabRow tabs={TOP_TABS} value={top} onChange={setTop} />
      {subs && (
        <div style={{ padding: "1.5rem 0 0" }}>
          <TabRow tabs={subs} value={currentSub} onChange={changeSub} counts={data?.counts || {}} late={data?.late} />
        </div>
      )}
      {top === "Dashboard" ? (
        // M20 (Pratik) fills the Dashboard.
        <Empty text="No Data Available" />
      ) : (
        <>
          {banner && (
            <div className={css.banner} style={{ background: banner[0][0], borderColor: banner[0][1], color: banner[0][2] }}>
              {banner[1]}
            </div>
          )}
          <div className={css.actionbar}>
            <div className={css.counts}>
              <p>
                {shown.length} {currentSub || top}
              </p>
              {emailNotSent > 0 && (
                <p>
                  <a href="#" style={{ color: "#B42318" }} onClick={(e) => { e.preventDefault(); setSel({ Email: ["Email not sent"] }); }}>
                    {emailNotSent} Email not sent
                  </a>
                </p>
              )}
              {waitingSanction > 0 && (
                <p>
                  <a href="#" style={{ color: "#B54708" }} onClick={(e) => { e.preventDefault(); setSel({ Sanction: ["Waiting for Sanction"] }); }}>
                    {waitingSanction} Waiting for Sanction
                  </a>
                </p>
              )}
            </div>
            <div className={css.filters}>
              <FilterBy key={`${top}|${currentSub}`} cats={cats} cards={inTab} sel={sel} setSel={setSel} date={date} setDate={setDate} dateLabel={DATE_OF[top]?.[0]} />
              <div className={css.row} style={{ gap: "1.5rem", flexWrap: "nowrap" }}>
                <p className={css.flab}>Sort by</p>
                <select className={css.sel} aria-label="Sort by" value={sort} onChange={(e) => setSort(e.target.value)}>
                  {(SORTS[top] || []).map((o) => (
                    <option key={o}>{o}</option>
                  ))}
                </select>
              </div>
              <div className={css.search}>
                <input placeholder="Search by Lead Name Or Unit No" aria-label="Search by lead name or unit number" value={q} onChange={(e) => setQ(e.target.value)} />
              </div>
            </div>
          </div>
          {nSel > 0 && (
            <div className={css.fchips}>
              {dateOn && (
                <span className={css.mchip}>
                  {DATE_OF[top][0]}: {date.from || "…"} – {date.to || "…"}{" "}
                  <button type="button" aria-label="Remove the date filter" onClick={() => setDate({ from: "", to: "" })}>
                    ✕
                  </button>
                </span>
              )}
              {Object.entries(sel).flatMap(([k, v]) =>
                v.map((x) => (
                  <span key={k + x} className={css.mchip}>
                    {k}: {x}{" "}
                    <button type="button" aria-label={`Remove ${x}`} onClick={() => setSel((s) => ({ ...s, [k]: s[k].filter((y) => y !== x) }))}>
                      ✕
                    </button>
                  </span>
                )),
              )}
              <button type="button" className={css.clearAll} onClick={clearFilters}>
                Clear all
              </button>
            </div>
          )}
          {state.loading && !data ? (
            <p className={css.hint} style={{ textAlign: "center", padding: "4rem 0", fontSize: "1.6rem" }}>
              Loading…
            </p>
          ) : state.error ? (
            <Empty text={state.error} />
          ) : shown.length ? (
            <div className={css.cards}>
              {shown.map((c) => (
                <Card key={c.allotmentPaymentId} card={c} tab={tabSlug} menu={menuFor(c, top, canRemoveLoyalty, m5perm)} onMenu={(item) => onMenu(item, c)} onAct={onAct} can={m5can} onTag={canDecide(c) ? (card, what) => setPop({ item: what === "accept" ? "Accept" : "Reject", card }) : undefined} onClaim={canClaim ? (card) => setPop({ item: "Claim", card }) : undefined} />
              ))}
            </div>
          ) : (
            <Empty text="No Allotment Available" />
          )}
        </>
      )}

      {/* Today's pop-ups, opened from the card ⋮ menu (M1.28) */}
      {pop?.item === "View CRM Details" && (
        <ViewAllotedCrmDetailsModal open selectedAllotmentObject={crmDetailsObject(pop.row)} handleClose={() => setPop(null)} />
      )}
      {pop?.item === "Timeline And Notes" && <CrmTimeLineModal open data={pop.row} handleClose={() => setPop(null)} />}
      {pop?.item === "Check Virtual Account Status" && <UpdateLeadDetails open leadDisplayId={pop.row.leaddisplayid} crm2LeadId={pop.card.leadId} handleClose={() => setPop(null)} />}
      {pop?.item === "Preview Agreement Confirmation Form" && (
        <PreviewAggrementConfirmationModal open allotment={pop.row} tabName={OLD_TAB[pop.card.crmStatusId] || "Agreement"} handleClose={() => setPop(null)} />
      )}
      {pop?.item === "Remove Loyalty Discount" && (
        <RemoveLoyaltyModal
          card={pop.card}
          onClose={() => setPop(null)}
          onDone={() => {
            setPop(null);
            load(top);
          }}
        />
      )}
      {/* M5-F2: today's Change Plan pop-up; CRM Review → av/submit, Approval Pending → approvals/:id/decide */}
      {(pop?.item === "plan" || pop?.item === "planView" || pop?.item === "avApprove") && (
        <ChangePriceModal
          open
          handleClose={(done) => {
            setPop(null);
            if (done === true && pop.item !== "planView") load(top);
          }}
          crmAllotmentObject={pop.row}
          tabName={pop.item === "avApprove" ? "Changed Plan" : "Change AV"}
          btnName={pop.item === "planView" ? "Preview" : "Preview And Approve"}
          sdrPercentage={pop.sdr?.sdrPercentage}
          legalCharges={pop.sdr?.legalCharges}
          registrationCharges={pop.sdr?.registrationCharges}
          crm2={{
            parkingFlow: pop.card.parkingFlow,
            submit: async (v) => {
              const res =
                pop.item === "avApprove"
                  ? await crm2DecideApproval(pop.extra.crm2ApprovalId, { decision: "approve", newBsp: v.newBsp, gstPercent: v.gstPercent, remark: v.remark, parkingUpgrade: v.parkingUpgrade })
                  : await crm2AvSubmit(pop.card.allotmentPaymentId, { newBsp: v.newBsp, plan: v.plan, remark: v.remark, skip: v.skip, parkingUpgrade: v.parkingUpgrade });
              successTost(pop.item === "avApprove" ? (res?.ocrPending ? "AV submitted successfully! OCR approval still pending" : "AV submitted successfully!") : planToast(res));
              return res;
            },
          }}
        />
      )}
      {pop?.item === "avReject" && (
        <RejectionReasonModal
          open
          handleClose={(r) => (r?.message !== undefined ? runM5("AV reject", () => crm2DecideApproval(pop.extra.crm2ApprovalId, { decision: "reject", remark: r.message }), "AV change rejected. Back to CRM Review") : setPop(null))}
        />
      )}
      {pop?.item === "ocrApprove" && (
        <ConfirmationAlert
          open
          close={() => setPop(null)}
          buttonTitle={"Cancel"}
          title={"Approve OCR"}
          subTitle={`OCR < ${Number(pop.extra?.ocrRequiredPercent) || 10}% for ${pop.card.leadName}. The customer has paid ${Number(pop.extra?.ocrPercentAtRequest || 0).toFixed(2)}% of the agreement value as OCR. Do you want to approve?`}
          returnRespButton={"Approve OCR"}
          confirm={(v) => (v === "confirmed" ? runM5("OCR approve", () => crm2DecideApproval(pop.extra.crm2ApprovalId, { decision: "approve" }), "OCR approved") : setPop(null))}
        />
      )}
      {pop?.item === "cuApprove" && (
        <ConfirmationAlert
          open
          close={() => setPop(null)}
          buttonTitle={"Cancel"}
          title={"Change Unit"}
          subTitle={`Change ${pop.card.leadName} from ${pop.extra?.from} to ${pop.extra?.to}? The booking goes back to CRM Review and the customer confirms the Application for Booking again by OTP. The 15 days do not restart.`}
          returnRespButton={"Approve"}
          confirm={(v) => (v === "confirmed" ? runM5("change unit approve", () => crm2ChangeUnitDecide(pop.extra.changeUnitRequestId, { decision: "approve" }), `Unit changed to ${pop.extra?.to}. Back to CRM Review`) : setPop(null))}
        />
      )}
      {pop?.item === "cuReject" && (
        <RejectionReasonModal
          open
          handleClose={(r) => (r?.message !== undefined ? runM5("change unit reject", () => crm2ChangeUnitDecide(pop.extra.changeUnitRequestId, { decision: "reject", remark: r.message }), "Change Unit rejected") : setPop(null))}
        />
      )}
      {pop?.item === "parking" && pop.card.parkingFlow === "OLD" && (
        <Crm2OldParkingModal
          card={pop.card}
          onClose={() => setPop(null)}
          onDone={() => {
            setPop(null);
            load(top);
          }}
        />
      )}
      {pop?.item === "parking" && pop.card.parkingFlow !== "OLD" && (
        <Crm2ParkingModal
          card={pop.card}
          onClose={() => setPop(null)}
          onDone={() => {
            setPop(null);
            load(top);
          }}
        />
      )}
      {pop?.item === "Change Unit" && (
        <Crm2ChangeUnitModal
          card={pop.card}
          onClose={() => setPop(null)}
          onDone={() => {
            setPop(null);
            load(top);
          }}
        />
      )}
      {pop?.item === "Move to CRM Review" && (
        <ConfirmationAlert
          open
          close={() => setPop(null)}
          buttonTitle={"Cancel"}
          title={"Warning"}
          subTitle={`Are you sure! Do you want to move ${pop.card.leadName} to CRM Review tab?`}
          returnRespButton={"Confirm"}
          confirm={(v) => (v === "confirmed" ? runM5("move to CRM Review", () => crm2MoveToCrmReview(pop.card.allotmentPaymentId), `${pop.card.leadName} moved to CRM Review`) : setPop(null))}
        />
      )}
      {pop?.item === "Change Booking Amount" && (
        <ChangeBookingAmountModal
          open
          allotment={{ ...pop.row, unitcost: pop.card.unitCost || pop.row.unitcost }}
          handleClose={() => setPop(null)}
          getCrmAllotmentsFromServer={() => load(top)}
          crm2={{ submit: (v) => crm2ChangeBookingAmount(pop.card.allotmentPaymentId, v) }}
        />
      )}
      {pop?.item === "Send Test Mail" && <EnterEmailModal open handleClose={(r) => (r?.flag && r?.email ? sendEmail(r.email) : setPop(null))} />}
      {pop?.item === "Claim" && (
        <ConfirmationAlert
          open
          close={() => setPop(null)}
          buttonTitle={"Cancel"}
          title={"Claim Lead"}
          subTitle={"Claim this lead? It will be assigned to you and moved to CRM Review. The Application for Booking email goes now."}
          returnRespButton={"Claim"}
          confirm={(value) => (value === "confirmed" ? decideTag("claim") : setPop(null))}
        />
      )}
      {pop?.item === "Accept" && (
        <ConfirmationAlert
          open
          close={() => setPop(null)}
          buttonTitle={"Cancel"}
          title={"Accept this booking?"}
          subTitle={`The Application for Booking email goes to the customer now and the ${pop.card.ring?.total || 15} days start.`}
          returnRespButton={"Accept"}
          confirm={(value) => (value === "confirmed" ? decideTag("accept") : setPop(null))}
        />
      )}
      {pop?.item === "Reject" && <RejectionReasonModal open handleClose={(r) => (r?.message !== undefined ? decideTag("reject", r.message) : setPop(null))} />}
      {pop?.item === "Send Email To Customer" && (
        <ConfirmationAlert
          open
          close={() => setPop(null)}
          buttonTitle={"Cancel"}
          title={pop.card.isEmailSent ? "Resend email?" : "Send email to the customer?"}
          subTitle={`Application for Booking email with the cost sheet goes to ${pop.card.leadName || "the customer"}. Check the customer email address first.`}
          returnRespButton={"Send"}
          confirm={(value) => (value === "confirmed" ? sendEmail(null) : setPop(null))}
        />
      )}
      {pop?.item === "View Cost Sheet" && (
        <ViewDocumentsModal
          open
          selectedAllotmentObject={{ ...pop.row, crmUserId: { crmUserDocUrl: pop.row.crm_user_doc_url } }}
          handleClose={() => setPop(null)}
          sendMailToLead={() => {}}
          isCompleteTab
          viewCostSheet
        />
      )}
    </div>
  );
}

export default Crm2Page;
